import { Interpolation, Theme } from '@mui/material'

declare module '@mui/material/styles/createTypography' {
  interface Typography {
    headingXXL: string
    headingXL: string
    headingL: string
    headingM: string
    headingS: string
    headingXS: string
    bodyL: string
    bodyM: string
    labelLJpNormal: string
    labelLJpTight: string
    labelMJpNormal: string
    labelMJpNormalMedium: string
    labelMJpTight: string
    labelSJpNormal: string
    labelSJpTight: string
    labelSJpTightRegular: string
    labelSJpNormalRegular: string
    labelXsJpNormal: string
    labelXsJpNormalRegular: string
    labelXsJpTight: string
    labelMEnNormal: string
    labelMEnTight: string
    labelSEnNormal: string
    labelSEnNormalMedium: string
    labelSEnTight: string
    labelXsEnNormal: string
    labelXsEnTight: string
    labelXsJpNormalMedium: string
  }

  interface TypographyOptions {
    headingXXL?: string
    headingXL?: string
    headingL?: string
    headingM?: string
    headingS?: string
    headingXS?: string
    bodyL?: string
    bodyM?: string
    labelLJpNormal?: string
    labelLJpTight?: string
    labelMJpNormal?: string
    labelMJpNormalMedium?: string
    labelMJpTight?: string
    labelSJpNormal?: string
    labelSJpTight?: string
    labelSJpTightRegular?: string
    labelSJpNormalRegular?: string
    labelXsJpNormal?: string
    labelXsJpNormalRegular?: string
    labelXsJpTight?: string
    labelMEnNormal?: string
    labelMEnTight?: string
    labelSEnNormal?: string
    labelSEnNormalMedium?: string
    labelSEnTight?: string
    labelXsEnNormal?: string
    labelXsEnTight?: string
    labelXsJpNormalMedium?: string
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    headingXXL: true
    headingXL: true
    headingL: true
    headingM: true
    headingS: true
    headingXS: true
    bodyL: true
    bodyM: true
    labelLJpNormal: true
    labelLJpNormalBold: true
    labelLJpTight: true
    labelMJpNormal: true
    labelMJpNormalMedium: true
    labelMJpTight: true
    labelSJpNormal: true
    labelSJpTight: true
    labelSJpTightRegular: true
    labelSJpNormalRegular: true
    labelXsJpNormal: true
    labelXsJpNormalRegular: true
    labelXsJpTight: true
    labelMEnNormal: true
    labelMEnTight: true
    labelSEnNormal: true
    labelSEnNormalMedium: true
    labelSEnTight: true
    labelXsEnNormal: true
    labelXsEnTight: true
    labelXsJpNormalMedium: true
    labelXsJpNormalBold: true
  }
}

export const typography: Record<
  | 'headingXXL'
  | 'headingXL'
  | 'headingL'
  | 'headingM'
  | 'headingS'
  | 'headingXS'
  | 'bodyL'
  | 'bodyM'
  | 'labelLJpNormal'
  | 'labelLJpNormalBold'
  | 'labelLJpTight'
  | 'labelMJpNormal'
  | 'labelMJpNormalMedium'
  | 'labelMJpTight'
  | 'labelSJpNormal'
  | 'labelSJpTight'
  | 'labelSJpTightRegular'
  | 'labelSJpNormalRegular'
  | 'labelXsJpNormal'
  | 'labelXsJpNormalRegular'
  | 'labelXsJpTight'
  | 'labelMEnNormal'
  | 'labelMEnTight'
  | 'labelSEnNormal'
  | 'labelSEnNormalMedium'
  | 'labelSEnTight'
  | 'labelXsEnNormal'
  | 'labelXsEnTight'
  | 'labelXsJpNormalMedium'
  | 'labelXsJpNormalBold',
  {
    fontSize: number
    fontWeight: number
    lineHeight: number
    letterSpacing: string | number
  }
> = {
  headingXXL: {
    fontSize: 48,
    fontWeight: 400,
    lineHeight: 1.167,
    letterSpacing: '0em',
  },
  headingXL: {
    fontSize: 34,
    fontWeight: 400,
    lineHeight: 1.235,
    letterSpacing: '0.00735em',
  },
  headingL: {
    fontSize: 24,
    fontWeight: 400,
    lineHeight: 1.334,
    letterSpacing: '0em',
  },
  headingM: {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: 1.6,
    letterSpacing: '0.0075em',
  },
  headingS: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.75,
    letterSpacing: '0.00938em',
  },
  headingXS: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.57,
    letterSpacing: '0.00714em',
  },
  bodyL: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
  },
  bodyM: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.43,
    letterSpacing: '0.01071em',
  },
  labelLJpNormal: {
    fontSize: 14,
    lineHeight: 0.24,
    letterSpacing: 0,
    fontWeight: 500,
  },
  labelLJpNormalBold: {
    fontSize: 15,
    lineHeight: 1.5,
    letterSpacing: 0,
    fontWeight: 700,
  },
  labelLJpTight: {
    fontSize: 14,
    lineHeight: 1.0,
    letterSpacing: 0,
    fontWeight: 500,
  },
  labelMJpNormal: {
    fontSize: 14,
    lineHeight: 0.24,
    letterSpacing: 0,
    fontWeight: 500,
  },
  labelMJpNormalMedium: {
    fontSize: 14,
    lineHeight: 1.5,
    letterSpacing: 0.4,
    fontWeight: 500,
  },
  labelMJpTight: {
    fontSize: 14,
    lineHeight: 1.0,
    letterSpacing: 0,
    fontWeight: 500,
  },
  labelSJpNormal: {
    fontSize: 13,
    lineHeight: 0.22,
    letterSpacing: 0,
    fontWeight: 500,
  },
  labelSJpTight: {
    fontSize: 13,
    lineHeight: 1.0,
    letterSpacing: 0,
    fontWeight: 500,
  },
  labelSJpTightRegular: {
    fontSize: 13,
    lineHeight: 1.0,
    letterSpacing: 0.46,
    fontWeight: 400,
  },
  labelSJpNormalRegular: {
    fontSize: 13,
    lineHeight: 1.5,
    letterSpacing: 0.46,
    fontWeight: 400,
  },
  labelXsJpNormal: {
    fontSize: 10,
    lineHeight: 0.2,
    letterSpacing: 0,
    fontWeight: 500,
  },
  labelXsJpNormalBold: {
    fontSize: 12,
    lineHeight: 1.5,
    letterSpacing: 0,
    fontWeight: 700,
  },
  labelXsJpNormalRegular: {
    fontSize: 12,
    lineHeight: 1.5,
    letterSpacing: 0,
    fontWeight: 400,
  },
  labelXsJpTight: {
    fontSize: 10,
    lineHeight: 1.0,
    letterSpacing: 0,
    fontWeight: 500,
  },
  labelMEnNormal: {
    fontSize: 14,
    lineHeight: 0.24,
    letterSpacing: 0,
    fontWeight: 500,
  },
  labelMEnTight: {
    fontSize: 14,
    lineHeight: 1.0,
    letterSpacing: 0,
    fontWeight: 500,
  },
  labelSEnNormal: {
    fontSize: 13,
    lineHeight: 0.22,
    letterSpacing: 0,
    fontWeight: 500,
  },
  labelSEnNormalMedium: {
    fontSize: 13,
    lineHeight: 1.5,
    letterSpacing: 0.06,
    fontWeight: 500,
  },
  labelSEnTight: {
    fontSize: 13,
    lineHeight: 1.0,
    letterSpacing: 0,
    fontWeight: 500,
  },
  labelXsEnNormal: {
    fontSize: 10,
    lineHeight: 1.4,
    letterSpacing: 0,
    fontWeight: 500,
  },
  labelXsEnTight: {
    fontSize: 10,
    lineHeight: 1.0,
    letterSpacing: 0,
    fontWeight: 500,
  },
  labelXsJpNormalMedium: {
    fontSize: 12,
    lineHeight: 1.5,
    letterSpacing: 0,
    fontWeight: 500,
  },
} as const

export const button: Record<'sizeL' | 'sizeM' | 'sizeS', Interpolation<{ theme: Theme }>> = {
  sizeL: {
    fontSize: 15,
    fontWeight: 500,
    lineHeight: '26px',
    letterSpacing: '0.03067em',
  },
  sizeM: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.02857em',
  },
  sizeS: {
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '22px',
    letterSpacing: '0.03538em',
  },
}
